import { Link } from "react-router-dom";
import { Globe, Phone } from "lucide-react";
import PageMeta from "./PageMeta";

const sponsors = [
  {
    name: "Sakuma",
    url: "https://sakuma.co.uk/",
    logo: "assets/sakuma_logo.jpg",
    banner: "assets/Sakuma-Super-Sharp-Banner-www-WEB.jpg",
    video: "assets/sakuma-vid.mp4",
    featured: true,
  },
  {
    name: "Reuben Heaton",
    url: "https://reubenheaton.com/",
    logo: "assets/ReubenHeaton_WHITE_SHORT_EST.png",
  },
  {
    name: "Lizard Tackle and Bait",
    url: "https://www.lizardtackleandbait.co.uk/",
    logo: "assets/lizard_bait&tackle.png",
  },
  {
    name: "Reefer Tackle and Bait",
    url: "https://share.google/SpFYXe9h9Y0xQR2KP",
    logo: "assets/reefers_logo.png",
  },
  {
    name: "Newtown Angling Centre",
    url: "http://www.newtownangling.com/",
    logo: "assets/newtown_logo.png",
  },
  {
    name: "David Dunstan Premier Baits",
    phone: "07770 688207",
    logo: null,
  },
  {
    name: "Penzance Angling Centre",
    url: "https://share.google/CbEYHNmOJIjgb7AP7",
    logo: "assets/penzance_angling_centre_logo.png",
  },
  {
    name: "WSB Tackle",
    url: "https://www.wsbtackle.com/",
    logo: "assets/wsb_tackle_logo.png",
  },
  {
    name: "AcuDocs",
    url: "http://www.acudocs.co.uk/",
    logo: "assets/acudocs_logo.png",
  },
  {
    name: "Westcountry Mines",
    url: "https://westcountrymines.co.uk/",
    logo: null,
    svgLogo: "westcountry",
  },
  {
    name: "Cornish Coffee",
    url: "https://cornishcoffee.co.uk",
    logo: "assets/cornish_coffee_logo.png",
    lightBg: true,
  },
  {
    name: "Protec Scaffolding",
    phone: "01209 711954 / 07747 498407",
    logo: "assets/protec.jpeg",
    lightBg: true,
  },
  {
    name: "Timber & Stone",
    url: "https://www.timberandstone.co.uk/",
    logo: "assets/timber-stone-logo.webp",
    lightBg: true,
  },
  {
    name: "Porthleven Boat Trips",
    url: "https://www.facebook.com/PoseidonPorthleven/",
    phone: "07366 883030",
    logo: "assets/Porthleven-Boat-Trips-Logo.png",
    lightBg: true,
  },
];

function FeaturedSponsorCard({ sponsor }) {
  return (
    <div className="col-span-full flex flex-col gap-6 rounded-2xl border border-cyan-300/30 bg-cyan-400/5 p-6 sm:p-8 panel-lift">
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between">
        <a
          href={sponsor.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4"
        >
          <div className="flex h-20 w-32 items-center justify-center rounded-xl border border-white/10 bg-white/5 px-3">
            <img
              src={sponsor.logo}
              alt={`${sponsor.name} logo`}
              className="max-h-14 max-w-full object-contain rounded-lg"
            />
          </div>
          <div>
            <p className="text-lg font-bold text-white">{sponsor.name}</p>
            {sponsor.url && (
              <span className="inline-flex items-center gap-1.5 text-xs text-cyan-300">
                <Globe className="h-3.5 w-3.5" />
                Visit website
              </span>
            )}
          </div>
        </a>
      </div>

      {sponsor.banner && (
        <a href={sponsor.url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-xl">
          <img src={sponsor.banner} alt={`${sponsor.name} banner`} className="w-full object-cover" />
        </a>
      )}

      {sponsor.video && (
        <video
          src={sponsor.video}
          controls
          autoPlay
          muted
          loop
          playsInline
          className="w-full rounded-xl"
        />
      )}
    </div>
  );
}

function SponsorCard({ sponsor }) {
  const inner = (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-8 text-center transition hover:border-cyan-300/30 hover:bg-cyan-400/5 h-full panel-lift">
      {/* Logo or placeholder */}
      <div className="flex h-24 w-full items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4">
        {sponsor.logo ? (
          <img
            src={sponsor.logo}
            alt={`${sponsor.name} logo`}
            className="max-h-16 max-w-full object-contain rounded-lg"
            style={sponsor.lightBg ? { background: '#f5f5f5', padding: '8px' } : {}}
          />
        ) : sponsor.svgLogo === "westcountry" ? (
          <svg viewBox="0 0 100 100" className="h-16 w-16" aria-label="Westcountry Mines logo">
            <circle cx="50" cy="50" r="50" fill="#FADF3F" />
            <text x="50" y="68" textAnchor="middle" fontSize="52" fontWeight="900" fontFamily="Arial, sans-serif" fill="#1e1e1e">W</text>
          </svg>
        ) : sponsor.svgLogo === "cornishcoffee" ? (
          <svg viewBox="0 0 100 100" className="h-16 w-16" aria-label="Cornish Coffee logo">
            <circle cx="50" cy="50" r="50" fill="#3b1a08" />
            {/* cup body */}
            <rect x="28" y="42" width="38" height="28" rx="4" fill="#c8793a" />
            {/* cup handle */}
            <path d="M66 50 Q80 50 80 60 Q80 70 66 70" fill="none" stroke="#c8793a" strokeWidth="4" strokeLinecap="round" />
            {/* saucer */}
            <ellipse cx="47" cy="72" rx="24" ry="4" fill="#a0561e" />
            {/* steam */}
            <path d="M38 38 Q40 30 38 24" fill="none" stroke="#e8c9a0" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M47 36 Q49 28 47 22" fill="none" stroke="#e8c9a0" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M56 38 Q58 30 56 24" fill="none" stroke="#e8c9a0" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          <span className="text-lg font-bold text-slate-300">{sponsor.name}</span>
        )}
      </div>

      {/* Name */}
      {(sponsor.logo || sponsor.svgLogo) && (
        <p className="text-sm font-semibold text-white">{sponsor.name}</p>
      )}

      {/* Link or phone */}
      {sponsor.url && (
        <span className="inline-flex items-center gap-1.5 text-xs text-cyan-300">
          <Globe className="h-3.5 w-3.5" />
          Visit website
        </span>
      )}
      {sponsor.phone && (
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
          <Phone className="h-3.5 w-3.5" />
          {sponsor.phone}
        </span>
      )}
    </div>
  );

  if (sponsor.url) {
    return (
      <a href={sponsor.url} target="_blank" rel="noopener noreferrer" className="block h-full">
        {inner}
      </a>
    );
  }

  return <div className="h-full">{inner}</div>;
}

export default function SponsorsPage() {
  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Our Sponsors | Redruth Sea Angling Association"
        description="Meet the sponsors supporting Redruth Sea Angling Association, Cornwall's sea angling club since 1962."
        keywords="RSAA sponsors, sea angling club sponsors Cornwall, Redruth Sea Angling Association sponsors"
        url="https://www.redruthseaanglingassociation.co.uk/sponsors"
      />
      <div className="mx-auto max-w-5xl px-6 py-16 sm:px-8 lg:px-12">

        <Link
          to="/"
          className="standalone-only inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20"
        >
          ← Back
        </Link>

        <div className="mt-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Redruth Sea Angling Association</p>
          <h1 className="heading-glow mt-3 text-4xl font-bold tracking-tight text-white sm:text-5xl">Our Sponsors</h1>
          <p className="mt-4 text-base leading-7 text-slate-400">
            RSAA is proud to be supported by these local tackle shops and businesses. Please support those who support us.
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sponsors.map((sponsor) =>
            sponsor.featured ? (
              <FeaturedSponsorCard key={sponsor.name} sponsor={sponsor} />
            ) : (
              <SponsorCard key={sponsor.name} sponsor={sponsor} />
            )
          )}
        </div>

        <p className="mt-12 text-center text-xs text-slate-500">
          Interested in sponsoring RSAA? Get in touch via the{" "}
          <Link to="/" className="text-cyan-400 hover:text-cyan-300 underline">contact section</Link> on our homepage.
        </p>

      </div>
    </div>
  );
}
